/* Centre de Conformité — tableau de bord + détail par bail.
 *
 * Trois onglets :
 *   1. Vue d'ensemble : score global + agrégation par immeuble / société
 *   2. Baux : liste triée par score croissant, filtrable par tone
 *   3. Alertes : toutes les alertes actionnables, groupées par sévérité
 *
 * Chaque alerte affiche un bouton d'action (link) permettant à l'utilisateur
 * de corriger le problème en un clic. Cette infrastructure alimente aussi
 * l'IA proactive de la Phase B.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import KpiCard from "@/components/common/KpiCard";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ShieldCheck, AlertTriangle, CheckCircle2, XCircle, ChevronRight,
  Building2, Briefcase, FileText, Sparkles,
} from "lucide-react";

const TONE_LABEL = { success: "Conforme", warning: "À surveiller", danger: "À risque" };

export default function CompliancePage() {
  const [dash, setDash] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selectedLease, setSelectedLease] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/compliance/dashboard").then(({ data }) => setDash(data));
    api.get("/compliance/alerts").then(({ data }) => setAlerts(data));
  }, []);

  useEffect(() => {
    if (selectedLease) {
      api.get(`/compliance/lease/${selectedLease}`).then(({ data }) => setDetail(data));
    } else setDetail(null);
  }, [selectedLease]);

  const filteredLeases = useMemo(() => {
    if (!dash) return [];
    if (filter === "all") return dash.leases;
    return dash.leases.filter((l) => l.tone === filter);
  }, [dash, filter]);

  if (!dash) return <div className="text-muted-foreground text-sm">Chargement du Centre de Conformité…</div>;

  const g = dash.global;

  return (
    <div className="animate-fade-up" data-testid="compliance-page">
      <PageHeader
        overline="Assistant patrimonial"
        title="Centre de Conformité"
        description="Score de conformité automatique par bail, immeuble et société. Alertes actionnables pour un patrimoine irréprochable."
      />

      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6 mb-8">
        <ScoreCard label="Score global" score={g.score} tone={g.tone} testid="compliance-global" />
        <KpiCard name="compliance-total" label="Baux suivis" icon={FileText} value={g.leases_total} />
        <KpiCard name="compliance-critical" label="Alertes critiques" icon={AlertTriangle}
                 value={g.critical_count}
                 tone={g.critical_count > 0 ? "danger" : "neutral"} />
        <KpiCard name="compliance-compliant" label="Dossiers conformes" icon={CheckCircle2}
                 value={`${g.leases_compliant} / ${g.leases_total}`} tone="accent" />
      </section>

      <Tabs defaultValue="overview">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="compliance-tab-overview">
            <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Vue d&apos;ensemble
          </TabsTrigger>
          <TabsTrigger value="leases" data-testid="compliance-tab-leases">
            <FileText className="h-3.5 w-3.5 mr-1.5" /> Baux ({g.leases_total})
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="compliance-tab-alerts">
            <AlertTriangle className="h-3.5 w-3.5 mr-1.5" /> Alertes ({alerts?.total || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid gap-6 lg:grid-cols-2">
            <BreakdownCard title="Par immeuble" icon={Building2} items={dash.buildings} />
            <BreakdownCard title="Par société" icon={Briefcase} items={dash.companies} />
          </div>
        </TabsContent>

        <TabsContent value="leases">
          <div className="flex gap-2 mb-4 flex-wrap">
            {["all", "success", "warning", "danger"].map((f) => (
              <Button key={f} variant={filter === f ? "default" : "outline"} size="sm"
                      onClick={() => setFilter(f)}
                      data-testid={`compliance-filter-${f}`}>
                {f === "all" ? "Tous" : TONE_LABEL[f]}
              </Button>
            ))}
          </div>
          <div className="space-y-2">
            {filteredLeases.map((l) => (
              <LeaseRow key={l.lease_id} lease={l} onOpen={() => setSelectedLease(l.lease_id)} />
            ))}
            {filteredLeases.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">Aucun bail correspondant.</div>
            )}
          </div>

          {detail && (
            <div className="mt-8 card-elevated p-6" data-testid="compliance-detail">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-overline">Détail de conformité</div>
                  <h3 className="font-heading text-xl mt-1">Bail {detail.lease_id.slice(0, 8)}</h3>
                </div>
                <ScoreBadge score={detail.score} tone={detail.tone} />
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {detail.rules.map((r, i) => (
                  <RuleRow key={`${r.code}-${i}`} rule={r} />
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="alerts">
          {!alerts ? (
            <div className="text-muted-foreground text-sm">Chargement…</div>
          ) : alerts.items.length === 0 ? (
            <div className="card-elevated p-12 text-center">
              <Sparkles className="mx-auto h-10 w-10 text-emerald-600 mb-4" />
              <div className="font-heading text-xl">Aucune alerte</div>
              <p className="text-sm text-muted-foreground mt-2">
                Votre patrimoine est en conformité. Continuez comme ça !
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {alerts.items.map((a, i) => <AlertRow key={i} alert={a} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ScoreCard({ label, score, tone, testid }) {
  const bg = tone === "success" ? "bg-emerald-50 border-emerald-200"
           : tone === "warning" ? "bg-amber-50 border-amber-200"
           : "bg-red-50 border-red-200";
  const color = tone === "success" ? "text-emerald-700"
              : tone === "warning" ? "text-amber-700"
              : "text-red-700";
  return (
    <div className={`card-elevated p-5 border-2 ${bg}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`mt-2 font-serif text-4xl ${color}`}>{score}%</div>
      <div className={`text-xs mt-1 ${color}`}>{TONE_LABEL[tone]}</div>
    </div>
  );
}

function BreakdownCard({ title, icon: Icon, items }) {
  return (
    <div className="card-elevated p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-4 w-4 text-accent" />
        <h3 className="font-heading">{title}</h3>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground">Aucune donnée.</div>
      ) : (
        <ul className="space-y-2">
          {items.map((it, idx) => (
            <li key={it.id || `bd-${idx}`} className="flex items-center gap-3 rounded-xl border border-border p-3">
              <ScoreBadge score={it.score} tone={it.tone} small />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{it.name}</div>
                <div className="text-xs text-muted-foreground">
                  {it.leases} bail{it.leases > 1 ? "s" : ""} · {it.criticals} critique{it.criticals > 1 ? "s" : ""}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ScoreBadge({ score, tone, small = false }) {
  const cls = tone === "success" ? "bg-emerald-100 text-emerald-800"
            : tone === "warning" ? "bg-amber-100 text-amber-800"
            : "bg-red-100 text-red-800";
  return (
    <div className={`${cls} font-mono font-bold rounded-full ${small ? "text-xs px-2 py-1" : "text-sm px-3 py-1.5"} flex items-center gap-1`}>
      {tone === "success" ? "🟢" : tone === "warning" ? "🟡" : "🔴"} {score}%
    </div>
  );
}

function LeaseRow({ lease, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full flex items-center gap-3 rounded-xl border border-border p-4 hover:border-accent hover:bg-accent/5 transition-colors text-left"
      data-testid={`compliance-lease-${lease.lease_id}`}
    >
      <ScoreBadge score={lease.score} tone={lease.tone} small />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">Bail {lease.lease_id.slice(0, 8)}</div>
        <div className="text-xs text-muted-foreground">
          {lease.critical_count} critique{lease.critical_count > 1 ? "s" : ""} · {lease.alert_count} alerte{lease.alert_count > 1 ? "s" : ""}
          {lease.top_alert && ` · ${lease.top_alert.label}`}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </button>
  );
}

function RuleRow({ rule }) {
  const Icon = rule.passed ? CheckCircle2 : rule.severity === "critical" ? XCircle : AlertTriangle;
  const color = rule.passed ? "text-emerald-600"
              : rule.severity === "critical" ? "text-red-600"
              : "text-amber-600";
  return (
    <div className={`flex items-start gap-2 p-2 rounded-lg ${rule.passed ? "" : "bg-secondary/30"}`}>
      <Icon className={`h-4 w-4 flex-shrink-0 mt-0.5 ${color}`} />
      <div className="flex-1 min-w-0 text-sm">
        <div className={rule.passed ? "text-muted-foreground" : "font-medium"}>{rule.label}</div>
        {rule.message && <div className="text-xs text-muted-foreground mt-0.5">{rule.message}</div>}
        {rule.action_href && (
          <Link to={rule.action_href} className="mt-1 inline-flex items-center gap-1 text-xs text-accent hover:underline">
            {rule.action_label || "Corriger"} <ChevronRight className="h-3 w-3" />
          </Link>
        )}
      </div>
    </div>
  );
}

function AlertRow({ alert }) {
  const cls = alert.severity === "critical" ? "border-red-300 bg-red-50"
            : "border-amber-300 bg-amber-50";
  return (
    <div className={`rounded-xl border ${cls} p-4`} data-testid={`compliance-alert-${alert.code}-${alert.lease_id}`}>
      <div className="flex items-start gap-3">
        <AlertTriangle className={`h-4 w-4 flex-shrink-0 mt-0.5 ${alert.severity === "critical" ? "text-red-600" : "text-amber-600"}`} />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">{alert.label}</div>
          <div className="text-xs mt-1">{alert.message}</div>
          <div className="text-[10px] text-muted-foreground mt-1">Bail {alert.lease_id.slice(0, 8)}</div>
        </div>
        {alert.action_href && (
          <Button asChild variant="outline" size="sm">
            <Link to={alert.action_href}>{alert.action_label || "Corriger"}</Link>
          </Button>
        )}
      </div>
    </div>
  );
}
